

import { z } from 'zod';
import { StructurePhaseSchema, STRUCTURE_PHASE_GUIDANCE } from '@/domains/story-planning/schemas/structure-phases';

/**
 * @fileoverview This file contains the canonical definitions for character sub-entities,
 * including the dynamically generated schema for a character snapshot.
 */


// =============================================================================
// CANONICAL FIELD DEFINITIONS - SINGLE SOURCE OF TRUTH
// =============================================================================

/**
 * The canonical source of truth for all fields that constitute a character's state.
 * Each object defines the field's key, its UI label, and distinct descriptions
 * for its two primary use cases: generating the character's initial state (Chapter 0)
 * and analyzing their state during the story's journey (in-story chapters).
 */
export const CHARACTER_FIELD_DEFINITIONS = [
    {
        key: 'plotRole',
        label: 'Summary',
        schemaDescription: "The character's main role in the story",
        initialStateDescription: "The character's planned core identity, motivation, and primary role in the plot before the story begins.",
        journeyDescription: "A summary of the character's role and function in the plot at the current point of the story.",
    },
    {
        key: 'age',
        label: 'Age',
        schemaDescription: "The character's age",
        initialStateDescription: "The character's age at the start of the story. Use numbers (e.g., '56') not words. Can include descriptive terms (e.g., 'early 80s', 'ancient', 'ageless').",
        journeyDescription: "The character's age at the current point of the story.",
    },
    {
        key: 'gender',
        label: 'Gender',
        schemaDescription: "The character's gender identity.",
        initialStateDescription: "The character's gender identity at the start of the story.",
        journeyDescription: "The character's gender identity at this point in the story.",
    },
    {
        key: 'species',
        label: 'Species',
        schemaDescription: "The character's species or race (e.g., Human, Elf, Android).",
        initialStateDescription: "The character's species or race at the start of the story. Default to human unless the genre or context indicates otherwise (e.g., fantasy, sci-fi).",
        journeyDescription: "The character's species or race at this point in the story.",
    },
    {
        key: 'height',
        label: 'Height',
        schemaDescription: "The character's height, in metric units (e.g. '188cm').",
        initialStateDescription: "The character's height at the start of the story. Use metric units (e.g., '175cm').",
        journeyDescription: "The character's height at this point of the story.",
    },
    {
        key: 'personality',
        label: 'Personality & Demeanour',
        schemaDescription: "The character's public personality and demeanour",
        initialStateDescription: "The character's core temperament, defining traits, flaws, virtues, demeanour, mannerisms and interaction style before the story starts (e.g., 'reserved and thoughtful', 'cynical but kind-hearted').",
        journeyDescription: "The character's core temperament, defining traits, flaws, virtues, demeanour, mannerisms and interaction style at this point of the story, incl. information about any traits that have been altered by the events in the story up to this point.",
    },
    {
        key: 'characterVoice',
        label: 'Voice',
        schemaDescription: "How the character speaks and expresses themselves in dialogue",
        initialStateDescription: "The character's distinctive speech patterns and verbal expression style: vocabulary level, sentence structure preferences, verbal tics or catchphrases, use of contractions, formality register, dialect or accent markers, how they express humor or emotion verbally. Focus on patterns that make their dialogue recognizable and distinct from other characters.",
        journeyDescription: "The character's speech patterns and verbal expression style at this point in the story, including any changes in how they speak due to story events (e.g., gained confidence, adopted new mannerisms, code-switching in different social contexts).",
    },
    {
        key: 'physicalDescription',
        label: 'Face & Body',
        schemaDescription: "A description of the character's physical appearance",
        initialStateDescription: "The character's physical appearance: build, body shape, athleticism, skin tone, facial features, hair color and style, eye color, posture, disabilities, and distinguishing features. Include culture/heritage/ethnicity when relevant. Focus on visually depictable attributes.",
        journeyDescription: "The character's physical appearance at the current time of the story, incl. information about any physical appearance that has been altered by the events in the story up to this point.",
    },
    {
        key: 'coreValues',
        label: 'Values & Boundaries',
        schemaDescription: "The character's main values and boundaries",
        initialStateDescription: "The character's fundamental worldview, core values and unbreakable rules before the story begins.",
        journeyDescription: "The character's fundamental worldview, core values and unbreakable rules at this point of the story, incl. information about any values and boundaries that have been altered by the events in the story up to this point.",
    },
    {
        key: 'clothing',
        label: 'Clothing & Attire',
        schemaDescription: "The character's clothing and attire",
        initialStateDescription: "The character's typical style of dress (armor, uniform, costume, etc.) and commonly worn accessories (glasses, jewelry) that reflect their personality, status, or culture. Exclude situational items.",
        journeyDescription: "The character's style of dress and accessories at this point of the story.",
    },
    {
        key: 'emotionalState',
        label: 'Emotional State',
        schemaDescription: "The character's emotional state",
        initialStateDescription: "The character's emotional state or mood as the story opens.",
        journeyDescription: "The character's emotional state at this point of the story.",
    },
    {
        key: 'physicalState',
        label: 'Physical State',
        schemaDescription: "The character's physical state",
        initialStateDescription: "The character's physical condition and health at the beginning of the narrative (e.g., fitness level, injuries, chronic conditions).",
        journeyDescription: "The character's physical condition at this point of the story.",
    },
    {
        key: 'keyKnowledgeGained',
        label: 'Existing Knowledge',
        schemaDescription: "The character's existing knowledge",
        initialStateDescription: "Relevant knowledge, information and/or secrets the character possesses before the story starts.",
        journeyDescription: "Relevant knowledge, information, secrets, or revelations the character possesses, incl. new information they have learned throughout the story.",
    },
    {
        key: 'updatedGoals',
        label: 'Current Goals',
        schemaDescription: "The character's current goals and objectives",
        initialStateDescription: "The character's primary wants, needs, and objectives as the story begins.",
        journeyDescription: "The character's primary wants, needs, and objectives at this point in the story.",
    }
] as const;


// =============================================================================
// DYNAMICALLY GENERATED SCHEMAS
// =============================================================================

/**
 * Builds the base character attributes schema from the canonical field definitions.
 * This is the foundation for all character-related schemas.
 * plotRole is required as it's the core identifier; all other fields are optional.
 */
const buildAttributesSchema = () => {
  const schemaShape = CHARACTER_FIELD_DEFINITIONS.reduce((acc, field) => {
    // plotRole is required, all other fields are optional
    if (field.key === 'plotRole') {
      acc[field.key] = z.string().describe(field.schemaDescription);
    } else {
      acc[field.key] = z.string().optional().describe(field.schemaDescription);
    }
    return acc;
  }, {} as Record<string, z.ZodString | z.ZodOptional<z.ZodString>>);

  return z.object(schemaShape as {
    plotRole: z.ZodString;
    age: z.ZodOptional<z.ZodString>;
    gender: z.ZodOptional<z.ZodString>;
    species: z.ZodOptional<z.ZodString>;
    height: z.ZodOptional<z.ZodString>;
    personality: z.ZodOptional<z.ZodString>;
    characterVoice: z.ZodOptional<z.ZodString>;
    physicalDescription: z.ZodOptional<z.ZodString>;
    coreValues: z.ZodOptional<z.ZodString>;
    clothing: z.ZodOptional<z.ZodString>;
    emotionalState: z.ZodOptional<z.ZodString>;
    physicalState: z.ZodOptional<z.ZodString>;
    keyKnowledgeGained: z.ZodOptional<z.ZodString>;
    updatedGoals: z.ZodOptional<z.ZodString>;
  });
};

/**
 * The base schema for character attributes, used by agents that generate or extract
 * character details. plotRole is required; all other attributes are optional.
 */
export const CharacterAttributesSchema = buildAttributesSchema();

/** @deprecated Use CharacterAttributesSchema instead */
export const CharacterInitialStateV2OutputSchema = CharacterAttributesSchema;

/**
 * Represents a complete, point-in-time snapshot of a character's state
 * at the end of a specific chapter. Extends CharacterAttributesSchema with metadata.
 */
export const CharacterSnapshotV2Schema = CharacterAttributesSchema.extend({
    id: z.string().describe("Unique identifier for this specific snapshot version."),
    chapterId: z.string().describe("ID of the chapter this snapshot corresponds to."),
    chapterNumber: z.number().describe("The number of the chapter."),
    timestamp: z.string().describe("ISO timestamp of when this snapshot was created or last updated."),
    majorCharacterEvents: z.array(z.object({
        id: z.string(),
        description: z.string(),
    })).optional().describe("A list of key events that happened to this character within this chapter."),
    /** @deprecated Use majorCharacterEvents instead. Kept for legacy data reads. */
    significantEvents: z.array(z.object({
        id: z.string(),
        description: z.string(),
    })).optional().describe("@deprecated Legacy field. Use majorCharacterEvents instead."),
    notPresentInChapter: z.boolean().optional().describe("Flag indicating this is a carry-forward snapshot because the character was not active in this chapter."),
    originChapterId: z.string().optional().describe("For carry-forward snapshots, the chapterId of the origin snapshot that was copied."),
    originContentHash: z.string().optional().describe("For carry-forward snapshots, a hash of the origin snapshot's content at the time of copy, used for staleness detection."),
    snapshotExpected: z.boolean().optional().describe("Flag indicating entity detection found this character in the chapter but snapshot generation failed."),
});


// =============================================================================
// STATIC SCHEMAS & TYPE INFERENCE
// =============================================================================

export const CharacterMomentV2Schema = z.object({
  id: z.string().describe("Unique identifier for the character moment."),
  chapterId: z.string().optional().describe("Reference to the chapter where this moment occurs."),
  chapterNumber: z.number().optional().describe("The chapter number where this moment occurs."),
  description: z.string().describe("Description of the significant moment in the character's journey."),
}).describe("A significant moment or event in a character's journey throughout the story, anchored to a specific chapter.");


export const CharacterChapterAnalysisV2Schema = z.object({
  characterName: z.string().describe("The name of the character this analysis pertains to."),
  stateSnapshot: CharacterAttributesSchema.extend({
      majorCharacterEvents: z.array(z.object({
          id: z.string(),
          description: z.string(),
      })).optional().describe("A list of key events that happened to this character within this chapter."),
    }).describe("A synthesized snapshot of the character's state at the conclusion of the chapter."),
});


// =============================================================================
// CHARACTER BEAT SCHEMA
// =============================================================================

/**
 * Helper to build semantic guidance for character beats.
 * Extracts character-specific guidance from the shared structure phase config.
 */
function buildCharacterBeatGuidance(): string {
  const phases = Object.entries(STRUCTURE_PHASE_GUIDANCE)
    .map(([phase, config]) => `• ${phase}: ${config.characterGuidance}`)
    .join('\n');
  return `Each beat represents an internal shift at a key structural moment.\n\nPhase guidance:\n${phases}`;
}

/**
 * A single beat in a character's arc, representing an internal/psychological shift
 * at a specific structural phase.
 *
 * Character beats focus on the INTERNAL journey (80%) with observable behavior (20%).
 * Multiple beats can exist at the same structurePhase if needed.
 */
export const CharacterBeatSchema = z.object({
  id: z.string().uuid().describe('Unique identifier for this beat.'),

  /** @deprecated Use globalPhase + arcPhase instead. Retained for backward compatibility with legacy data. */
  structurePhase: StructurePhaseSchema.optional().describe(
    'Deprecated. Use globalPhase and arcPhase instead. Legacy single-phase field retained for backward compatibility.'
  ),

  globalPhase: StructurePhaseSchema.optional().describe(
    'Where this beat sits in the OVERALL story timeline (the A-Story defines this timeline). ' +
    'Determines the column in the beat matrix. For main characters whose arc spans the whole story, ' +
    'globalPhase equals arcPhase. For characters introduced late or killed early, these differ.'
  ),

  arcPhase: StructurePhaseSchema.optional().describe(
    'The structural function this beat serves for THIS CHARACTER\'s internal arc. ' +
    'Determines the beat color in the matrix. A character introduced at the global Midpoint would ' +
    'have their first beat with globalPhase="Midpoint" but arcPhase="Initial State".'
  ),

  internalState: z.string().describe(
    'The character\'s emotional realization, decision, or psychological shift at this moment. ' +
    'Focus on the internal truth or lie they are confronting. ' +
    'Examples: "Realizes he cannot succeed alone", "Chooses duty over love", "Accepts his father\'s flaws", ' +
    '"Believes she is unworthy of love" (Initial State), "Understands that vulnerability is strength" (Final State).'
  ),

  behavior: z.string().optional().describe(
    'The observable action or behavior triggered by this internal shift (optional). ' +
    'This is how the internal state manifests externally. ' +
    'Examples: "Throws away the medal", "Calls his estranged father", "Walks away from the throne".'
  ),

  arcProgress: z.number().min(-1).max(1).describe(
    'The character\'s position on their truth/lie spectrum at this moment. ' +
    '+1 = Fully aligned with their deeper truth, embracing growth and authenticity. ' +
    '-1 = Fully regressed toward their wound, lie, or false belief. ' +
    '0 = Neutral, ambiguous, or at a crossroads. ' +
    'Typically starts near -1 or 0 at Initial State and moves toward +1 by Final State (for positive arcs).'
  ),
}).describe(
  'A beat in a character\'s arc representing an internal/psychological shift. ' +
  buildCharacterBeatGuidance()
);

export type CharacterBeat = z.infer<typeof CharacterBeatSchema>;

// =============================================================================
// CHARACTER PLANNED ARC SCHEMA
// =============================================================================

/**
 * The complete planned arc for a character, defined as a sequence of internal beats.
 *
 * This schema captures the character's psychological transformation journey through
 * the story's structural phases. Beats should include at minimum:
 * - Initial State: The character's starting psychological state, wound, or lie
 * - Final State: The character's ending state after transformation
 *
 * Additional beats at Inciting Incident, Midpoint, Climax, etc. flesh out the journey.
 */
export const CharacterPlannedArcV2Schema = z.object({
  id: z.string().describe('Unique identifier for the character arc.'),

  arcName: z.string().describe(
    'A short, evocative title for the character\'s arc. ' +
    'Examples: "The Path to Redemption", "From Coward to Hero", "The Corruption of Power".'
  ),

  arcSummary: z.string().describe(
    'A 1-3 sentence summary of the character\'s overall transformation. ' +
    'Should capture the essential journey from who they are at the start to who they become. ' +
    'Example: "Sarah begins as a cynical loner who believes relationships only bring pain. ' +
    'Through her bond with the orphan, she learns that vulnerability is the price of love."'
  ),

  beats: z.array(CharacterBeatSchema).describe(
    'The sequence of internal shifts that define this character\'s arc. ' +
    'Should include at minimum Initial State and Final State beats. ' +
    'Additional beats at key structural phases (Inciting Incident, Midpoint, Climax, etc.) ' +
    'flesh out the transformation journey. Each beat carries both a globalPhase (position in ' +
    'the overall story timeline) and an arcPhase (structural function for this character\'s arc).'
  ),
}).describe(
  'The complete character arc defining their psychological transformation through the story. ' +
  'Arcs are composed of beats—internal shifts at key structural moments—that trace the ' +
  'character\'s journey from their initial wound/lie toward (or away from) their deeper truth.'
);

export type CharacterPlannedArcV2 = z.infer<typeof CharacterPlannedArcV2Schema>;
export type CharacterAttributes = z.infer<typeof CharacterAttributesSchema>;

// =============================================================================
// SHARED PROMPT SECTIONS FOR CHARACTER ARC AGENTS
// =============================================================================

/**
 * Shared prompt text describing character arc concepts.
 * Used by character-arc-creation-v2 and character-arc-extractor-v3 to ensure
 * consistent guidance across generation and extraction flows.
 */
export const CHARACTER_ARC_CONCEPTS_PROMPT = `\
A character arc traces the **internal journey**—the psychological, emotional, and moral transformation. Each beat captures:

- **internalState**: The character's emotional realization, decision, or psychological shift
- **behavior** (optional): The observable action triggered by this internal shift
- **arcProgress**: Position on the truth/lie spectrum (-1 to +1)

### Dual-Phase Model (globalPhase vs arcPhase)
Each character beat carries TWO phase assignments:

- **globalPhase**: Where the beat sits in the OVERALL story timeline. The A-Story defines this timeline.
- **arcPhase**: What structural function the beat serves for THIS CHARACTER's internal arc. A character introduced at the global Midpoint would have their first beat with globalPhase="Midpoint" but arcPhase="Initial State".

**Rules:**
- For characters whose arc spans the entire story: globalPhase typically equals arcPhase.
- For characters introduced late, killed early, or with compressed arcs: arcPhase will differ from globalPhase. Their arc has its own internal structure independent of when it occurs in the global timeline.
- Think of globalPhase as "when" and arcPhase as "what role in this character's transformation."`;

/**
 * Shared prompt text for the structure phases table.
 * Used by character-arc-creation-v2 and character-arc-extractor-v3.
 */
export const STRUCTURE_PHASES_TABLE_PROMPT = `\
Character beats align with the story's structural phases. These phases apply to BOTH globalPhase (timeline position) and arcPhase (arc function):

| Phase | Description | Character Guidance |
|-------|-------------|-------------------|
| **Initial State** | The character's starting point | What lie do they believe? What wound do they carry? What do they want vs. need? |
| **Inciting Incident** | Something disrupts their world | What event challenges their worldview or forces them from comfort? |
| **Break Into Two** | They commit to the journey | What choice shows commitment to change? What do they leave behind? |
| **Midpoint** | A revelation or shift | They stop reacting and start acting. What truth do they glimpse? |
| **Break Into Three** | The lowest point | They confront their deepest fear. The lie is fully exposed. |
| **Climax** | The defining choice | They prove their transformation by making a choice their old self couldn't make. |
| **Final State** | The new normal | How have they changed? What truth do they now embody? |`;

/**
 * Shared prompt text for arc progress value guidance.
 * Used by character-arc-creation-v2 and character-arc-extractor-v3.
 */
export const ARC_PROGRESS_PROMPT = `\
The **arcProgress** tracks the character's position on their truth/lie spectrum:

- **+1**: Fully aligned with their deeper truth (growth, authenticity)
- **-1**: Fully regressed toward their wound/lie/false belief
- **0**: Neutral, ambiguous, or at a crossroads

Typical progression for a **positive arc**: starts near -1 or 0, dips at Break Into Three, ends near +1.
For a **negative arc** (corruption/fall): may start near 0, end near -1.
For a **flat arc** (steadfast character): stays relatively stable, testing beliefs without changing them.`;

// Legacy type exports for backwards compatibility during migration
/** @deprecated Use CharacterBeat instead */
export type TurningPoint = { description: string; chapterNumber?: number };
/** @deprecated Use CharacterBeat[] with structurePhase instead */
export type KeyTurningPoints = {
  incitingIncident: TurningPoint;
  midpointShift: TurningPoint;
  climaxRevelation: TurningPoint;
  resolutionState: TurningPoint;
};

